import React, { useState, useEffect } from 'react';
import { FiChevronDown } from 'react-icons/fi';

/**
 * FAQSection — reusable FAQ block that auto-emits JSON-LD FAQPage schema
 * directly into <head> via useEffect (bypasses Helmet's inline-script quirks).
 *
 * Feeds AI search engines (ChatGPT / Perplexity / Gemini / Google AI Overviews)
 * and traditional Google People-Also-Ask boxes with structured Q&A pairs.
 */
export const FAQSection = ({
  title = 'Frequently Asked Questions',
  subtitle,
  items = [],
  testid = 'faq',
  compact = false,
  className = '',
}) => {
  const [openIdx, setOpenIdx] = useState(null);

  // Emit FAQPage JSON-LD directly to <head> — reliable across all browsers
  useEffect(() => {
    if (!items || !items.length) return undefined;
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: String(a).replace(/<[^>]*>/g, '').trim(),
        },
      })),
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-testid', `${testid}-schema`);
    el.text = JSON.stringify(schema).replace(/</g, '\\u003c');
    document.head.appendChild(el);
    return () => {
      if (el.parentNode) el.parentNode.removeChild(el);
    };
  }, [items, testid]);

  if (!items || !items.length) return null;

  const Wrap = compact ? 'div' : 'section';

  return (
    <Wrap
      data-testid={`${testid}-block`}
      className={compact
        ? `${className}`
        : `py-16 bg-white border-t border-gray-100 ${className}`}
    >
      <div className={compact ? '' : 'max-w-4xl mx-auto px-6 md:px-12'}>
        {!compact && title && (
          <div className="text-center mb-10">
            <p className="text-[#F57C20] text-xs tracking-[0.3em] uppercase font-semibold mb-2">
              Answers to your questions
            </p>
            <h2 className="text-3xl sm:text-4xl text-[#1A1A2E] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              {title}
            </h2>
            {subtitle && (
              <p className="text-[#6B7280] text-sm max-w-2xl mx-auto">{subtitle}</p>
            )}
          </div>
        )}

        {compact && title && (
          <h3 className="text-lg text-[#1A1A2E] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            {title}
          </h3>
        )}

        <div className="space-y-2">
          {items.map((faq, i) => (
            <div
              key={i}
              className="bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-[#F57C20]/30 transition-colors"
            >
              <button
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                data-testid={`${testid}-item-${i}`}
                className="w-full text-left px-5 py-3 flex items-center justify-between hover:bg-[#F7F8FA] transition-colors"
              >
                <span className="text-[#1A1A2E] text-sm font-medium pr-4">{faq.q}</span>
                <FiChevronDown
                  className={`w-4 h-4 text-[#9CA3AF] flex-shrink-0 transition-transform ${openIdx === i ? 'rotate-180' : ''}`}
                />
              </button>
              {openIdx === i && (
                <div className="px-5 pb-4">
                  {typeof faq.a === 'string' ? (
                    <p className="text-[#6B7280] text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.a }} />
                  ) : (
                    <div className="text-[#6B7280] text-sm leading-relaxed">{faq.a}</div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Wrap>
  );
};

export default FAQSection;
